import React, { useEffect, useMemo, useState } from 'react';
import {
  PageHeader, DataTable, Tag, Tooltip, Button, Dialog, Field, TextInput, Checkbox, Banner, useCkToast, ckConfirm,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../../services/api.js';
import { FormGrid, Row, apiError } from '../../next/ui';

/** תפקידים והרשאות — new design. Same endpoints and permission model as RolesPermissions (/roles). */
const ALL_RESOURCES = [
  { key: 'members', label: 'חברים' }, { key: 'exams', label: 'מבחנים' }, { key: 'examRegistration', label: 'הרשמות למבחנים' },
  { key: 'grades', label: 'ציונים' }, { key: 'messages', label: 'הודעות' }, { key: 'kolels', label: 'כוללים' },
  { key: 'drashas', label: 'דרשות' }, { key: 'subscriptions', label: 'מנויים' }, { key: 'reports', label: 'דוחות' },
  { key: 'auditLogs', label: 'יומן ביקורת' }, { key: 'featureFlags', label: 'Feature Flags' }, { key: 'emailDomains', label: 'דומיין לשליחת מיילים' }, { key: 'roles', label: 'תפקידים' },
  { key: 'users', label: 'משתמשים' }, { key: 'narrationPrompts', label: 'קטלוג קריינות' }, { key: 'ivr', label: 'IVR / ימות' },
  { key: 'examCity', label: 'ערי מבחן' }, { key: 'examPeriod', label: 'זמני מבחנים' }, { key: 'labels', label: 'מדבקות וברקודים' },
  { key: 'studyCalendar', label: 'לוח שנה' },
];
const ALL_ACTIONS = ['read', 'create', 'update', 'delete'];
const ACTION_LABELS = { read: 'קריאה', create: 'יצירה', update: 'עדכון', delete: 'מחיקה' };
const resLabel = (k) => ALL_RESOURCES.find((r) => r.key === k)?.label || k;

const permissionsToMap = (permissions = []) => {
  const map = {};
  permissions.forEach((p) => { map[p.resource] = map[p.resource] || {}; p.actions.forEach((a) => { map[p.resource][a] = true; }); });
  return map;
};
const mapToPermissions = (map) => Object.keys(map)
  .map((resource) => ({ resource, actions: Object.keys(map[resource]).filter((a) => map[resource][a]), fieldRestrictions: { denied: [], readOnly: [] } }))
  .filter((p) => p.actions.length > 0);

function PermsSummary({ permissions = [] }) {
  if (!permissions.length) return <span className="ck-muted">ללא הרשאות</span>;
  const full = permissions.filter((p) => p.actions.length === 4);
  const partial = permissions.filter((p) => p.actions.length > 0 && p.actions.length < 4);
  return (
    <span className="nx-item__tags">
      {full.map((p) => <Tag key={p.resource} tone="primary" icon="check">{resLabel(p.resource)}</Tag>)}
      {partial.map((p) => (
        <Tooltip key={p.resource} title={p.actions.map((a) => ACTION_LABELS[a] || a).join(', ')}>
          <Tag>{resLabel(p.resource)}</Tag>
        </Tooltip>
      ))}
    </span>
  );
}

export default function RolesPermissionsNext() {
  const toast = useCkToast();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [editRole, setEditRole] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });
  const [permMap, setPermMap] = useState({});
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const loadRoles = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await api.get('/roles');
      setRoles(data.data || []);
    } catch (e) {
      setLoadError(apiError(e, 'שגיאה בטעינת תפקידים'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadRoles(); }, []);

  const openNew = () => { setEditRole(null); setForm({ name: '', description: '' }); setPermMap({}); setError(''); setDialogOpen(true); };
  const openEdit = (role) => { setEditRole(role); setForm({ name: role.name, description: role.description }); setPermMap(permissionsToMap(role.permissions)); setError(''); setDialogOpen(true); };

  const togglePerm = (resource, action) => setPermMap((prev) => ({ ...prev, [resource]: { ...(prev[resource] || {}), [action]: !prev[resource]?.[action] } }));
  const toggleRow = (resource) => setPermMap((prev) => {
    const all = ALL_ACTIONS.every((a) => prev[resource]?.[a]);
    return { ...prev, [resource]: Object.fromEntries(ALL_ACTIONS.map((a) => [a, !all])) };
  });
  const toggleColumn = (action) => setPermMap((prev) => {
    const all = ALL_RESOURCES.every((r) => prev[r.key]?.[action]);
    const next = { ...prev };
    ALL_RESOURCES.forEach((r) => { next[r.key] = { ...(next[r.key] || {}), [action]: !all }; });
    return next;
  });
  const selectAll = () => setPermMap(Object.fromEntries(ALL_RESOURCES.map((r) => [r.key, Object.fromEntries(ALL_ACTIONS.map((a) => [a, true]))])));

  const save = async () => {
    setError('');
    setSaving(true);
    const payload = { name: form.name, description: form.description, permissions: mapToPermissions(permMap) };
    try {
      if (editRole) { await api.put(`/roles/${editRole._id}`, payload); toast.success('התפקיד עודכן בהצלחה'); }
      else { await api.post('/roles', payload); toast.success('התפקיד נוצר בהצלחה'); }
      setDialogOpen(false);
      loadRoles();
    } catch (e) {
      setError(apiError(e, 'שגיאה'));
    } finally {
      setSaving(false);
    }
  };
  const remove = async (role) => {
    if (!(await ckConfirm({ title: 'מחיקת תפקיד', message: `למחוק את התפקיד «${role.name}»?`, confirmLabel: 'מחיקה' }))) return;
    try {
      await api.delete(`/roles/${role._id}`);
      toast.success('התפקיד נמחק');
      loadRoles();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה במחיקה'));
    }
  };

  const countOf = (res) => ALL_ACTIONS.filter((a) => permMap[res]?.[a]).length;
  const colCount = (action) => ALL_RESOURCES.filter((r) => permMap[r.key]?.[action]).length;
  const totalChecked = useMemo(() => ALL_RESOURCES.reduce((n, r) => n + countOf(r.key), 0), [permMap]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <PageHeader title="תפקידים והרשאות" sub={`${roles.length} תפקידים — מה כל תפקיד רשאי לראות ולערוך במערכת`} primary={{ label: 'תפקיד חדש', onClick: openNew }} />
      <DataTable
        mode="client"
        rows={roles}
        rowKey="_id"
        loading={loading}
        error={loadError}
        onRetry={loadRoles}
        onRefresh={loadRoles}
        selectable={false}
        storageKey="bchinot-roles"
        exportFilename="תפקידים"
        emptyTitle="אין תפקידים"
        onRowClick={openEdit}
        rowActions={(r) => [{ key: 'edit', icon: 'edit', label: 'עריכה', onClick: () => openEdit(r) }]}
        rowMenu={(r) => [
          { key: 'edit', label: 'עריכה', icon: 'edit', onClick: () => openEdit(r) },
          { divider: true },
          { key: 'del', label: 'מחיקה', icon: 'trash', danger: true, onClick: () => remove(r) },
        ]}
        columns={[
          { key: 'name', label: 'שם', render: (r) => <b>{r.name}</b> },
          { key: 'description', label: 'תיאור' },
          { key: 'permissions', label: 'הרשאות', width: 460, getValue: (r) => (r.permissions || []).map((p) => resLabel(p.resource)).join(', '), render: (r) => <PermsSummary permissions={r.permissions} /> },
        ]}
      />

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editRole ? `עריכת תפקיד — ${editRole.name}` : 'תפקיד חדש'}
        icon="key"
        size="lg"
        footer={(
          <>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>ביטול</Button>
            <Button variant="primary" disabled={!form.name.trim() || saving} onClick={save}>{saving ? 'שומר…' : editRole ? 'עדכון' : 'צור תפקיד'}</Button>
          </>
        )}
      >
        <div className="nx-stack">
          {error ? <Banner tone="danger" icon="alert" onClose={() => setError('')}>{error}</Banner> : null}
          <FormGrid cols={2}>
            <Field label="שם התפקיד"><TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-autofocus /></Field>
            <Field label="תיאור"><TextInput value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          </FormGrid>
          <Row justify="space-between">
            <h3 className="nx-sub-h">{`הרשאות `}<span className="ck-muted">{`(${totalChecked} / ${ALL_RESOURCES.length * ALL_ACTIONS.length} נבחרו)`}</span></h3>
            <Row>
              <Button size="sm" icon="checkAll" onClick={selectAll}>סמן הכל</Button>
              <Button size="sm" variant="ghost" onClick={() => setPermMap({})}>נקה הכל</Button>
            </Row>
          </Row>
          <div className="nx-matrix">
            <table>
              <thead>
                <tr>
                  <th>משאב</th>
                  {ALL_ACTIONS.map((a) => (
                    <th key={a}>
                      <div className="nx-stack" style={{ gap: 4, alignItems: 'center' }}>
                        <span>{ACTION_LABELS[a]}</span>
                        <Checkbox checked={colCount(a) === ALL_RESOURCES.length} indeterminate={colCount(a) > 0 && colCount(a) < ALL_RESOURCES.length} onChange={() => toggleColumn(a)} aria-label={`כל ה${ACTION_LABELS[a]}`} />
                      </div>
                    </th>
                  ))}
                  <th>הכל</th>
                </tr>
              </thead>
              <tbody>
                {ALL_RESOURCES.map((res) => (
                  <tr key={res.key}>
                    <td>{res.label}</td>
                    {ALL_ACTIONS.map((a) => (
                      <td key={a}><Checkbox checked={!!permMap[res.key]?.[a]} onChange={() => togglePerm(res.key, a)} aria-label={`${res.label} — ${ACTION_LABELS[a]}`} /></td>
                    ))}
                    <td><Checkbox checked={countOf(res.key) === 4} indeterminate={countOf(res.key) > 0 && countOf(res.key) < 4} onChange={() => toggleRow(res.key)} aria-label={`${res.label} — הכל`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Dialog>
    </>
  );
}
